import styled from '@emotion/styled';
import BayTooltip from '../../BayTooltip';
import { useMemo } from 'react';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';

type PlatformType = 'social' | 'client' | 'financial';

interface MenuToggleProps {
	collapsed: boolean;
	setCollapseState: (collapsed: boolean) => void;
	platformType?: PlatformType;
}

/*
 * Toggler and IconWrapper MUST be module-level, not defined inside MenuToggle.
 * A styled-component created inside a function body is a brand-new component
 * type on every render. React diffs by type, so any re-render swaps in a "new"
 * Toggler and unmounts/remounts the real DOM node -- and Tooltip's
 * onVisibleChange fires exactly on the hover transition, re-rendering MenuToggle
 * at the exact moment the handle is hovered. The remount drops the browser's
 * own :hover tracking on that element, so it reads as "vanishes the instant you
 * hover it," no CSS involved. Neither template below interpolates props, so
 * hoisting them is a pure fix with no behavior change.
 */
const Toggler = styled.div`
	height: 62px;
	width: 24px;
	/*
	 * Hidden-by-default state uses opacity, NOT display, and pointer-events
	 * is deliberately left at its default \`auto\` here -- do not add
	 * \`pointer-events: none\`.
	 *
	 * This element is \`.menu-toggler\`, and sidenav.scss reveals it on hover:
	 *
	 *   .sideNav-layout:hover .menu-toggler { opacity: 1; }
	 *
	 * display:none was tried first and had to be reverted: an element with
	 * display:none generates no box, so it cannot be hit-tested -- it can
	 * never receive its OWN :hover/:focus, which made the self-sustaining
	 * hover rule below a no-op and let the handle vanish the instant the
	 * cursor left the ancestor's hover region (see git history on this file,
	 * four same-day commits chasing this).
	 *
	 * \`pointer-events: none\` was then tried on top of that and ALSO had to be
	 * reverted: this handle sits entirely OUTSIDE \`.sideNav-layout\`'s box
	 * (right: -24px), over the host app's content area. With pointer-events
	 * none, the browser skips this element during hit-testing and hands the
	 * event to whatever's underneath -- the page content, not
	 * \`.sideNav-layout\`. Reached for from the content side (the natural
	 * direction, since it hangs over that content), the ancestor's :hover
	 * never fires, and the invisible handle -- excluded from hit-testing --
	 * can't trigger its own reveal either. It becomes permanently
	 * unreachable from that side. Opacity alone keeps the box hit-testable
	 * while invisible, so hovering it from ANY direction genuinely works.
	 */
	opacity: 0;
	border-left: 23px solid var(--bs-white);
	border-top: 13px solid transparent;
	border-bottom: 15px solid transparent;
	position: absolute;
	right: -24px;
	top: 0.85rem;
	/*
	 * The handle hangs 24px OUTSIDE the nav's right edge, so it sits over the
	 * host app's content area rather than over the nav. With no z-index it is
	 * \`auto\`, and any positioned element in the host's layout that paints later
	 * covers it -- visible, but every click lands on whatever is on top. That is
	 * exactly what happened in smc while client, whose layout has nothing
	 * stacked there, worked fine.
	 *
	 * \`sticky\` is the right step: this is app chrome that must clear page
	 * content but stay under every overlay (offcanvas starts at 1045). Do not
	 * swap this for a bare integer -- see docs/css-unification/overlay-and-zindex.md.
	 */
	z-index: var(--bs-zindex-sticky, 1020);

	/*
	 * Keep itself visible while it is the thing being hovered.
	 *
	 * This handle is \`position: absolute; right: -24px\`, so it sits OUTSIDE
	 * \`.sideNav-layout\`'s box. Because the hidden state above is opacity-based
	 * (not display:none), this element stays hit-testable the whole time, so
	 * hovering it directly genuinely sustains its own visibility here --
	 * unlike a display:none default, which can never receive a hover in the
	 * first place. \`:focus-visible\` does the same for keyboard users, who could
	 * otherwise tab to a handle that is never painted.
	 *
	 * Do not "fix" this by widening the ancestor's hover target -- the handle
	 * hangs outside the nav on purpose.
	 */
	&:hover,
	&:focus,
	&:focus-visible {
		opacity: 1;
		cursor: pointer;
	}

	/*
	 * Small screens use MobileToggler instead -- this one should be fully
	 * inert there, not just invisible, so pointer-events:none is correct
	 * here specifically (no discoverability tradeoff: it is never meant to
	 * be reachable at this width at all).
	 */
	@media only screen and (max-width: 991px) {
		opacity: 0;
		pointer-events: none;
	}
`;

/*
 * THIS BOX MUST BE EXACTLY THE GLYPH. It is the tooltip's reference element, and
 * Floating UI centres on the reference's border box -- so any slack between this
 * box and the chevron inside it shows up directly as tooltip offset.
 *
 * As a plain block div it was neither: its width was the Toggler's full 24px
 * content width with the 16px icon at the left of it, and its height was a ~24px
 * LINE box with the icon sitting on a baseline rather than filling it. The tooltip
 * centred on that box, which is why it came out high and to the left of the chevron.
 *
 * `flex` removes the line box (the icon becomes a flex item rather than inline
 * content on a baseline) and the explicit size matches the icon's own `size`, so
 * the box and the glyph are the same rectangle.
 */
const ICON_SIZE = 16;

const IconWrapper = styled.div`
	position: relative;
	left: -21px;
	top: 7px;
	display: flex;
	width: ${ICON_SIZE}px;
	height: ${ICON_SIZE}px;
`;

const MenuToggle = ({ collapsed, setCollapseState, platformType }: MenuToggleProps) => {
	/*
	 * No React state tracking the tooltip's own hover here on purpose.
	 * rc-tooltip/rc-trigger can run fully uncontrolled -- it manages its own
	 * show/hide internally on 'hover'. Wiring that back into a controlled
	 * `visible` prop forced MenuToggle to re-render on every hover in/out,
	 * which is what caused the handle to vanish the instant it was hovered
	 * (see the module-scope comment on Toggler above: any re-render here used
	 * to redefine Toggler as a new component and remount the real DOM node,
	 * dropping the browser's :hover on it mid-hover). Letting the tooltip own
	 * its own visibility removes that re-render entirely.
	 */
	// `overlay` was an inline JSX literal, so it was a brand-new element on
	// every render of MenuToggle -- not just the hover-driven ones already
	// removed above, but any unrelated re-render from the parent (SideNav
	// re-rendering off Redux state, for example) while the tooltip happens to
	// be open. rc-trigger treats a changed popup-content reference as a
	// reason to re-measure/reposition, so with the mouse not having moved
	// that read as the popup glitching/jittering in place -- most visible
	// right at the boundary between the handle and the popup above it, i.e.
	// hovering just above the caret. Memoized so its reference only changes
	// when what it actually renders changes.
	const overlay = useMemo(
		() => (
			<span id="tooltip-collapse-icon" className={platformType === 'social' ? 'social-menu-toggle' : 'client-menu-toggle'}>
				{collapsed ? 'Expand' : 'Collapse'}
			</span>
		),
		[collapsed, platformType],
	);

	return (
		/*
		 * THE TOOLTIP WRAPS THE ICON, NOT THE TOGGLER. This is the fix for
		 * "the collapse tooltip floats up and to the left of the handle", and it
		 * is structural -- do not replace it with a coordinate.
		 *
		 * Floating UI anchors to its reference element's border box. `Toggler` is
		 * a CSS-triangle: 24x62 of content plus `border-left: 23px`,
		 * `border-top: 13px` and `border-bottom: 15px`, so its border box is 47px
		 * wide and 90px tall. `IconWrapper` then pushes the visible chevron a
		 * further `left: -21px; top: 7px` OUT of that box.
		 *
		 * So the glyph you can see is not inside its own trigger in either axis,
		 * and NO `placement` value can land on it -- `top` measures from a box
		 * edge ~20px above and ~23px left of the chevron. That is the whole
		 * reason the old engine needed 60 lines of absolute viewport coordinates
		 * in sidenav.scss.
		 *
		 * Anchoring to `IconWrapper` makes the reference the glyph itself, so
		 * `placement="top"` is correct by construction with no offsets at all.
		 *
		 * The CLICK target is unchanged -- still the full 62px `Toggler`, which
		 * is what makes the handle easy to grab. Only the tooltip's anchor moved.
		 */
		<Toggler
			className="menu-toggler"
			onClick={() => setCollapseState(!collapsed)}
		>
			<BayTooltip
				// `animation="zoom"` and `zIndex={9999}` removed: the new engine owns its
				// transition, and its z-index comes from `var(--bs-zindex-tooltip)` rather than a
				// bare integer. Both were silently ignored, so leaving them read as configuration
				// that did something.
				mouseEnterDelay={0.25}
				mouseLeaveDelay={0}
				overlay={overlay}
				placement="top"
				// `trigger` is a STRING here, not rc-tooltip's array. BayTooltip gates its
				// hover interaction on `trigger === 'hover'`, so `['hover']` would compare
				// false and silently disable hover entirely -- the tooltip would simply never
				// open, with nothing in the console.
				trigger="hover"
			>
				<IconWrapper>
					{collapsed ? (
						<BsChevronRight size={ICON_SIZE} />
					) : (
						<BsChevronLeft size={ICON_SIZE} />
					)}
				</IconWrapper>
			</BayTooltip>
		</Toggler>
	);
};

export default MenuToggle;
